import { ActionTypes as AppActionTypes } from '../actions/app'
import { ActionTypes as UserActions } from '../actions/user'
import { Sizes } from '../constants'

export const nonSelectedEstate = 'NO_ESTATE_SELECTED'

const initialState = {
    dashboard: {
        data: {},
        isWorking: false,
    },
    /**
     * @type {import('../types').Estate}
     */
    estates: [],
    keepSessionAlive: false,
    selectedEstate: nonSelectedEstate,
    tables: {},
    uploadProgress: null,
    currentfileName: '',
    window: {
        height: window.innerHeight,
        width: window.innerWidth,
    },
}

function appReducer(state = initialState, { type, payload = {} }) {
    switch (type) {
        case 'persist/REHYDRATE':
            const { app: lastState = {} } = payload
            return {
                ...initialState,
                ...state,
                ...lastState,
            }
        case 'KEEP_SESSION_ALIVE':
            return {
                ...state,
                keepSessionAlive: payload.value,
            }
        case UserActions.LOGIN_SUCCESS: {
            let { estates = [] } = payload

            // Remove all these non active estates
            estates = estates.filter(({ active }) => active === true || typeof active !== 'boolean')

            return {
                ...state,
                estates,
            }
        }
        case UserActions.LOGIN_SESSION_SUCCESS: {
            const { estates: payloadEstates = [] } = payload.data
            // Remove all these non active estates
            const estates = payloadEstates.filter(({ active }) => active)

            return {
                ...state,
                estates,
                selectedEstate: estates.length ? estates[0].id : initialState.selectedEstate,
            }
        }
        case UserActions.LOGOUT_SUCCESS:
            return {
                ...state,
                estates: [],
                selectedEstate: initialState.selectedEstate,
            }
        case AppActionTypes.SELECT_ESTATE:
            return {
                ...state,
                selectedEstate: payload.estateId,
            }
        case 'WINDOW_RESIZE':
            return {
                ...state,
                window: {
                    ...state.window,
                    height: payload.height,
                    width: payload.width,
                },
            }
        case AppActionTypes.DASHBOARD_STATS_FETCH_REQUEST: {
            return {
                ...state,
                dashboard: {
                    ...state.dashboard,
                    isWorking: true,
                },
            }
        }
        case AppActionTypes.DASHBOARD_STATS_FETCH_FAILURE: {
            return {
                ...state,
                dashboard: {
                    ...state.dashboard,
                    isWorking: false,
                },
            }
        }
        case AppActionTypes.DASHBOARD_STATS_FETCH_SUCCESS: {
            return {
                ...state,
                dashboard: {
                    ...state.dashboard,
                    isWorking: false,
                    data: {
                        ...payload,
                    },
                },
            }
        }
        case AppActionTypes.SAVE_TABLE_COLUMNS: {
            return {
                ...state,
                tables: {
                    ...state.tables,
                    [payload.tableKey]: payload.columns,
                },
            }
        }
        case AppActionTypes.CLEAR_UPLOAD_PROGRESS: {
            return {
                ...state,
                uploadProgress: initialState.uploadProgress,
                currentfileName: initialState.currentfileName,
            }
        }
        case AppActionTypes.SAVE_UPLOAD_PROGRESS: {
            return {
                ...state,
                uploadProgress: payload.progress,
                currentfileName: payload.currentfileName,
            }
        }
        default:
            return state
    }
}

const isHeightLG = ({ height }) => height > Sizes.height.lg

const isHeightXL = ({ height }) => height > Sizes.height.xl

const isHeightXXL = ({ height }) => height > Sizes.height.xxl

const isWidthSM = ({ width }) => width > Sizes.width.sm

const isWidthMD = ({ width }) => width > Sizes.width.md

const isWidthLG = ({ width }) => width > Sizes.width.lg

const isWidthXL = ({ width }) => width > Sizes.width.xl

const selectIsHeightLG = (state) => state?.app?.window?.height > Sizes.height.lg

const selectIsHeightXL = (state) => state?.app?.window?.height > Sizes.height.xl

const selectIsHeightXXL = (state) => state?.app?.window?.height > Sizes.height.xxl

const selectIsWidthSM = (state) => state?.app?.window?.width > Sizes.width.sm

const selectIsWidthMD = (state) => state?.app?.window?.width > Sizes.width.md

const selectIsWidthLG = (state) => state?.app?.window?.width > Sizes.width.lg

const selectIsWidthXL = (state) => state?.app?.window?.width > Sizes.width.xl

const selectIsMobile = (state) => !selectIsWidthMD(state)

/**
 * Get estates list
 * @param {Object} state Current estate
 * @returns {Array<Object>} Estates array
 */
function getEstates(state = {}) {
    const { app } = state
    const { estates } = app || {}

    return estates
}

/**
 * 
 * @param {import('.').RootState} state 
 * @returns {import('../types').Estate | undefined}
 */
const getSelectedEstate = (state = {}) => {
    const { app } = state
    const { estates, selectedEstate } = app || {}

    if (selectedEstate === nonSelectedEstate) {
        return null
    }

    return estates.find(({ id }) => id === selectedEstate)
}

const getSelectedEstateId = (state = {}) => {
    const { app } = state
    const { selectedEstate } = app || {}

    return selectedEstate
}

/**
 * Gets all the dashboards in the current estate
 * @param {import('.').RootState} state Redux global state
 */
function getDashboards(state = {}) {
    const { dashboards = [] } = getSelectedEstate(state) || {}
    return dashboards
}

/**
 * Checks if a dashboard has permission to be displayed in selected estate
 * @param {Object} state Redux global state
 * @param {String} dashboardId Dashboard ID
 */
function isDashboardInSelectedEstate(state = {}, dashboardId) {
    const { dashboards = [] } = getSelectedEstate(state) || {}
    return dashboards.includes(dashboardId)
}

/**
 * Get selected column for a given table key
 * @param {Object} state
 * @param {String} tableKey
 */
function getTableColumns(state, tableKey) {
    const tables = state?.app?.tables || {}
    return tables[tableKey]
}

function getUploadProgress(state) {
    return { progress: state.app.uploadProgress, currentfileName: state.app.currentfileName }
}

export {
    getDashboards,
    getEstates,
    getSelectedEstate,
    getSelectedEstateId,
    getTableColumns,
    getUploadProgress,
    isDashboardInSelectedEstate,
    isHeightLG,
    isHeightXL,
    isHeightXXL,
    isWidthSM,
    isWidthMD,
    isWidthLG,
    isWidthXL,
    selectIsHeightLG,
    selectIsHeightXL,
    selectIsHeightXXL,
    selectIsWidthSM,
    selectIsWidthMD,
    selectIsWidthLG,
    selectIsWidthXL,
    selectIsMobile,
}

export default appReducer
